///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../../position';
import { GtthStepGroupEnum } from '../../../../../generated/shared-types.model';
import { E2eTestUtils } from '../../../../../../testing/e2e-test-utils';
import { SectorConstants } from './sector.constants';
import { GtthSectorObstruction } from '../sector-obstruction.enum';
import { AngleUtils } from '../../angle-utils';
import { TestMarkerView } from '../../marker/test/test-marker-view';

export class SectorFeatureView {
    testMarker: TestMarkerView;
    public sectorFillOpacity: number;

    public sectorStrokeOpacity: number;
    private googleMap: google.maps.Map;

    private sector: google.maps.Polygon;
    private obstructionPolyline: google.maps.Polyline;
    private sectorColor;
    private strokeColor;
    private angleDelta = 0;
    private sectorRotate = false;
    private sectorCenterLatLng: google.maps.LatLng;
    private isSelected: boolean;

    private sectorPath: google.maps.LatLng[];
    private obstructionPath: google.maps.LatLng[];

    private isActiveWithLinks = false;
    private iaActiveWithoutLinks = false;

    constructor(
        private locationName: string,
        private readonly sectorInDevice: number,
        private readonly p2mpRepSectorIndex: number,
        private readonly sectorCenter: GtthPosition,
        private angle: number,
        private radius: number,
        private frequency: number,
        private isUserForced: boolean,
        private color: string,
        private readonly obstructionAngle: number,
        private readonly obstructionDirection: GtthSectorObstruction
    ) {}

    setParams(locationName: string, angle: number, radius: number, frequency: number, isUserForced: boolean, color: string): void {
        this.locationName = locationName;
        this.angle = angle;
        this.radius = radius;
        this.frequency = frequency;
        this.isUserForced = isUserForced;
        this.color = color;
    }

    setActiveWithLinks(isActiveWithLinks: boolean): void {
        this.isActiveWithLinks = isActiveWithLinks;
    }

    setActiveWithoutLinks(iaActiveWithoutLinks: boolean): void {
        this.iaActiveWithoutLinks = iaActiveWithoutLinks;
    }

    draw(googleMap: google.maps.Map, isUseEngineColors: boolean): void {
        this.googleMap = googleMap;

        this.setSectorColor(isUseEngineColors);
        this.drawSector();
    }

    setAngle(angle: number): void {
        this.angle = angle;
        this.recalculateArcPath(this.angle);
        this.sector.setPath(this.sectorPath);
        if (this.obstructionPolyline && this.obstructionPath?.length) {
            this.obstructionPolyline.setPath(this.obstructionPath);
        }
    }

    setIsUserForced(isForced: boolean): void {
        this.isUserForced = isForced;
    }

    setCustomColor(customColor: string): void {
        this.color = customColor;
    }

    triggerSelected(isSelected: boolean, isUseEngineColors: boolean): void {
        this.isSelected = isSelected;
        this.changeColor(isUseEngineColors);
    }

    setIsInActiveLayer(isInActiveLayer: boolean, selectedStep: GtthStepGroupEnum): void {
        if (isInActiveLayer) {
            this.sectorFillOpacity = selectedStep === GtthStepGroupEnum.LOCATIONS_AND_LOSES ? 0.1 : 0.3;
            this.sectorStrokeOpacity = 1;
        } else {
            this.sectorFillOpacity = SectorConstants.NOT_ACTIVE_LAYER_FILL_OPACITY;
            this.sectorStrokeOpacity = SectorConstants.NOT_ACTIVE_LAYER_STROKE_OPACITY;
        }

        if (this.iaActiveWithoutLinks) {
            this.sectorFillOpacity = 0.05;
            this.sectorStrokeOpacity = 0.7;
        }
    }

    hide(): void {
        this.destroyListeners();
        this.sector.setMap(null);
        this.sector = null;
        if (this.obstructionPolyline) {
            this.obstructionPolyline.setMap(null);
            this.obstructionPolyline = null;
        }
        if (this.testMarker) {
            this.testMarker.hide();
            this.testMarker = undefined;
        }
    }

    changeColor(isUseEngineColors: boolean): void {
        this.setSectorColor(isUseEngineColors);
        this.sector.setOptions({
            fillColor: this.sectorColor,
            strokeColor: this.strokeColor,
            fillOpacity: this.sectorFillOpacity,
            strokeOpacity: this.sectorStrokeOpacity
        });
        if (this.obstructionPolyline) {
            this.obstructionPolyline.setOptions(this.getObstructionPolylineOptions());
        }
    }

    registerClickListener(callback: (event: google.maps.MapMouseEvent) => any): void {
        this.sector?.addListener('click', callback);
        this.testMarker?.registerClickListener(callback);
    }

    registerRightClickListener(callback: (event: google.maps.MapMouseEvent) => any): void {
        this.sector?.addListener('rightclick', callback);
        this.testMarker?.registerRightClickListener(callback);
    }

    registerDoubleClickListener(callback: (event: google.maps.MapMouseEvent) => any): void {
        this.sector?.addListener('dblclick', callback);
    }

    addRotationListener(eventName: string, callback: (event: google.maps.MapMouseEvent) => void): void {
        this.sector?.addListener(eventName, callback);
    }

    destroyListeners(): void {
        google.maps.event.clearInstanceListeners(this.sector);
    }

    destroyListener(eventType: string): void {
        google.maps.event.clearListeners(this.sector, eventType);
    }

    public startSectorRotate(mouseEvent): void {
        if (this.sectorRotate || !this.isSelected) {
            return;
        }
        this.getSectorMouseDelta(mouseEvent);
        this.googleMap.setOptions({ draggable: false });
        this.sector.setOptions({ zIndex: 2000 });
        this.sectorRotate = true;
    }

    public sectorRotation(mouseEvent): number {
        if (!this.sectorRotate) {
            return;
        }

        const mouseAngle = AngleUtils.normalize(google.maps.geometry.spherical.computeHeading(this.sectorCenterLatLng, mouseEvent.latLng));

        const newAngle = Math.round(mouseAngle + this.angleDelta);

        if (Math.abs(this.angle - newAngle) < 1) {
            return undefined;
        }
        return newAngle;
    }

    public endSectorRotate(): boolean {
        if (!this.sectorRotate) {
            return false;
        }
        this.sectorRotate = false;
        this.googleMap.setOptions({ draggable: true });
        this.sector.setOptions({ zIndex: this.getSectorZIndex() });

        return true;
    }

    public getSectorMouseDelta(mouseEvent): void {
        this.angleDelta =
            this.angle - ((360 + google.maps.geometry.spherical.computeHeading(this.sectorCenterLatLng, mouseEvent.latLng)) % 360);
    }

    private drawSector(): void {
        this.sectorCenterLatLng = this.sectorCenter.toLatLng(google);

        this.recalculateArcPath(this.angle);

        if (!this.sector) {
            this.sector = new google.maps.Polygon();
        }

        this.sector.setOptions({
            map: this.googleMap,
            fillColor: this.sectorColor,
            fillOpacity: this.sectorFillOpacity,
            strokeOpacity: this.sectorStrokeOpacity,
            strokeWeight: this.isUserForced ? 2 : 1,
            strokeColor: this.strokeColor,
            draggable: false,
            zIndex: this.getSectorZIndex(),
            path: this.sectorPath
        } as google.maps.MapOptions);

        if (this?.obstructionPath?.length) {
            if (!this.obstructionPolyline) {
                this.obstructionPolyline = new google.maps.Polyline();
            }

            this.obstructionPolyline.setOptions(this.getObstructionPolylineOptions());
        }

        if (E2eTestUtils.getShowLineMarkers()) {
            this.testMarker = new TestMarkerView(
                this.sectorPath[Math.round(this.sectorPath.length / 2)].toJSON(),
                `${E2eTestUtils.getShowLineMarkers()} sector ${this.locationName} ${this.sectorInDevice}`
            );
            this.testMarker.draw(this.googleMap);
        }
    }

    private getObstructionPolylineOptions(): google.maps.PolylineOptions {
        return {
            map: this.googleMap,
            path: this.obstructionPath,
            strokeOpacity: 0,
            icons: [
                {
                    icon: {
                        path: 'M 0,-1 0,1',
                        strokeColor: this.strokeColor,
                        strokeOpacity: 1,
                        strokeWeight: this.isUserForced ? 2 : 1,
                        scale: 2
                    },
                    offset: '0',
                    repeat: '10px'
                }
            ]
        } as google.maps.PolylineOptions;
    }

    private recalculateArcPath(angle: number): void {
        this.sectorPath = [];
        this.obstructionPath = [];

        let startAngle = angle - SectorConstants.SECTOR_WIDTH_ANGLE / 2;
        let endAngle = angle + SectorConstants.SECTOR_WIDTH_ANGLE / 2;

        if (this.obstructionAngle > 0) {
            if (this.obstructionDirection === GtthSectorObstruction.LEFT) {
                this.obstructionPath = [this.sectorCenterLatLng, ...this.createArcPath(startAngle, startAngle + this.obstructionAngle)];
                startAngle += this.obstructionAngle;
            } else {
                this.obstructionPath = [...this.createArcPath(endAngle - this.obstructionAngle, endAngle), this.sectorCenterLatLng];
                endAngle -= this.obstructionAngle;
            }
        }

        this.sectorPath = [this.sectorCenterLatLng, ...this.createArcPath(startAngle, endAngle)];
    }

    private createArcPath(startAngle: number, endAngle: number): google.maps.LatLng[] {
        const points = [];
        for (let iterAngle = startAngle; iterAngle <= endAngle; iterAngle += SectorConstants.SECTOR_ARC_STEP) {
            points.push(google.maps.geometry.spherical.computeOffset(this.sectorCenterLatLng, this.radius, iterAngle));
        }
        return points;
    }

    private getSectorZIndex(): number {
        return this.isActiveWithLinks ? 2 : 1;
    }

    private setSectorColor(isUseEngineColors: boolean): void {
        this.sectorColor = SectorConstants.SECTOR_DEFAULT_COLOR;
        this.strokeColor = SectorConstants.SECTOR_DEFAULT_COLOR;

        if (this.isSelected) {
            this.sectorColor = SectorConstants.SECTOR_SELECTED_COLOR;
            this.strokeColor = SectorConstants.SECTOR_SELECTED_COLOR;
        } else if (isUseEngineColors) {
            this.strokeColor = this.color;
            this.sectorColor = this.color;
        } else {
            switch (this.frequency) {
                case 58320:
                    this.sectorColor = SectorConstants.SECTOR_58320_COLOR;
                    break;
                case 60480:
                    this.sectorColor = SectorConstants.SECTOR_60480_COLOR;
                    break;
                case 62640:
                    this.sectorColor = SectorConstants.SECTOR_62640_COLOR;
                    break;
                case 64800:
                    this.sectorColor = SectorConstants.SECTOR_64800_COLOR;
                    break;
            }

            this.strokeColor = this.isUserForced ? SectorConstants.SECTOR_FORCED_COLOR : this.sectorColor;
        }

        if (this.iaActiveWithoutLinks) {
            this.sectorColor = SectorConstants.SECTOR_BOM_ACTIVE_COLOR;
        }
    }
}
